import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  EntityType,
  EntityTypeEnum,
  EntityState,
  Index,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  WorkflowActions,
  EntityIdAndType
} from 'api'
import { createWorkflowReferenceDataEntitySelector, createWorkflowReferenceDataAvailableEventsSelector } from 'selectors/workflowSelectors'
import { Locale, Translation } from 'locale'
import { noop } from 'lodash'
import { IndexDialogComponent } from './IndexDialogComponent'

export type IndexEditDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  entityId: string
  onClose?: () => unknown
}

interface StateProps {
  indexEntity: ReferenceDataEntityType<Index> | null
  availableEvents: ReferenceDataManagementEvent[]
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: Index; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: IndexEditDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: IndexEditDialogProps) => ownProps.entityId

const mapStateToProps = createStructuredSelector({
  indexEntity: createWorkflowReferenceDataEntitySelector(EntityType.INDEX, workflowIdSelector, entityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const IndexEditDialogContainer: React.FC<IndexEditDialogProps> = props => {
  const { workflowId, indexEntity, availableEvents, onUpdate, onSubmitWorkflow, onCancelWorkflow, onClose = noop } = props

  if (!indexEntity) {
    return null
  }

  const index = indexEntity.data
  const title = indexEntity.entityState === EntityState.NEW ? Locale.translate(Translation.TC_NEW_INDEX) : index.code
  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)

  const handleUpdate = (partialIndex: Partial<Index>) => {
    onUpdate({
      entityId: index.id,
      entityType: EntityTypeEnum.INDEX,
      updatedEntity: { ...index, ...partialIndex },
      workflowId
    })
  }

  const handleSave = () => {
    onSubmitWorkflow(workflowId)
    onClose()
  }

  const handleClose = () => {
    onCancelWorkflow(workflowId)
    onClose()
  }

  return (
    <IndexDialogComponent
      title={title}
      readOnly={readOnly}
      index={index}
      validationIssues={indexEntity.validationIssues ?? []}
      onClose={handleClose}
      onSave={handleSave}
      onUpdate={handleUpdate}
    />
  )
}

export const IndexEditDialog = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(IndexEditDialogContainer)
